import React from 'react';
import { Text } from '../../../components/Donee/components/common/Text/Text';
import DeepLinkCopiedModal from '../../../components/Donee/components/CheckoutWizard/ThankYouWizard/DeepLinkCopiedModal';
import {
    clickEvent,
    successEvent,
    viewEvent,
} from '../../../hooks/PushEvents/clevertapEvents';
import { assetUrl } from '../../../utils/webConfig';
import { HeartLoading } from '../../../components/HeartLoading';
import { GivelifyLogo } from '../../../components/icons';
import QRCodeSection from '../../../components/GetAppModal/QRCodeSection';
import { Button } from '../../../components/ui';
import type { useEventPage } from './useEventPage';

const EVENT_IMAGE_SRC = assetUrl('/images/png/event-image.png');

const CheckIcon: React.FC = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
    >
        <g clipPath="url(#clip0_12987_89150)">
            <path
                opacity="0.12"
                d="M20 10C20 4.47715 15.5228 0 10 0C4.47715 0 0 4.47715 0 10C0 15.5228 4.47715 20 10 20C15.5228 20 20 15.5228 20 10Z"
                fill="#F85520"
            />
            <path
                d="M6.5625 10L8.7355 12.5L13.4365 7.5"
                stroke="#F85520"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </g>
        <defs>
            <clipPath id="clip0_12987_89150">
                <rect
                    width="20"
                    height="20"
                    fill="white"
                />
            </clipPath>
        </defs>
    </svg>
);

const clickEventPayload = {
    action: 'clicked',
    location: 'Event',
    name: 'GoToAppStore',
} as const;

const EventHeading: React.FC = () => (
    <Text className="mb-4 text-heading-small">
        Get the Givelify app to view event details
    </Text>
);

const ticketSteps = [
    'Keep this ticket and future tickets in one place',
    'Discover upcoming events from this organization',
    'Stay connected after the event is over',
];
const eventSteps = [
    'Discover upcoming events from this organization',
    'Keep future event tickets in one place',
    'Stay connected after the event is over',
];

const EventSteps: React.FC<{ eventId: string | null }> = ({ eventId }) => (
    <>
        {(eventId ? eventSteps : ticketSteps).map((step, index) => (
            <div
                key={index}
                className="flex items-start gap-2 mb-[10px] [&>svg]:shrink-0 [&>svg]:mt-0.5"
            >
                <CheckIcon />
                <Text className="text-xs leading-5">{step}</Text>
            </div>
        ))}
    </>
);

type EventPageViewProps = Omit<ReturnType<typeof useEventPage>, 'metaTags'>;

export const EventPageView: React.FC<EventPageViewProps> = ({
    eventId,
    donorLoading,
    url,
    showCopiedModal,
    isCopying,
    qrCodeValue,
    handleGetApp,
    handleDownload,
    handleCloseCopiedModal,
}) => {
    if (donorLoading) {
        return (
            <div className="p-10 bg-[#fafafc] min-h-screen mobile:p-6">
                <HeartLoading
                    maxWidth={500}
                    className="heart-loading"
                />
            </div>
        );
    }

    return (
        <div className="p-10 bg-[#fafafc] min-h-screen mobile:p-6">
            <div className="block mobile:hidden">
                <GivelifyLogo className="block mx-auto mt-4 mb-8" />
                <div className="flex rounded-[32px] overflow-hidden max-w-[1000px] mx-auto shadow-high">
                    <div className="flex-1 p-8 bg-[#fafafc] flex flex-col">
                        <EventHeading />
                        <EventSteps eventId={eventId} />
                        <img
                            className="w-full h-auto block mt-10"
                            src={EVENT_IMAGE_SRC}
                            alt="Givelify event"
                        />
                    </div>
                    <div className="flex-1 p-8 bg-white flex items-center justify-center text-center">
                        <QRCodeSection
                            className="[&>div]:text-base [&>div]:font-semibold [&>div]:leading-6"
                            value={qrCodeValue}
                        />
                    </div>
                </div>
            </div>
            <div className="hidden mobile:flex mobile:flex-col">
                <GivelifyLogo className="block mx-auto mt-4 mb-8" />
                <img
                    className="w-full h-auto block mb-10"
                    src={EVENT_IMAGE_SRC}
                    alt="Givelify event"
                />
                <EventHeading />
                <EventSteps eventId={eventId} />
                <div className="my-10">
                    <Button
                        id="event-get-app"
                        loading={isCopying}
                        onClick={handleGetApp}
                        className="w-full"
                    >
                        Get the app
                    </Button>
                </div>
            </div>
            <DeepLinkCopiedModal
                url={url}
                open={showCopiedModal}
                clickEvent={clickEvent}
                viewEvent={viewEvent}
                successEvent={successEvent}
                clickEventPayload={clickEventPayload}
                title="Get the free Givelify app"
                onClick={handleDownload}
                onClose={handleCloseCopiedModal}
            />
        </div>
    );
};
